import { createAssistantToolContext } from '@/features/assistant/tool-context'
import { serializeAssistantTransportValue } from '@/features/assistant/transport-value'
import { normalizeAssistantToolError } from '@/features/assistant/tool-errors'
import { getLocalAgentBridge } from './local-agent-bridge-runtime'
import {
  createLocalAgentSamplingResponseEvent,
  LOCAL_AGENT_EVENT_NAMES,
} from './local-agent-events'
import { LOCAL_AGENT_PROTOCOL_VERSION, type LocalAgentBridgeLifecyclePayload } from './local-agent-protocol'
import {
  LOCAL_AGENT_NODE_DEFAULT_BASE_URL,
  LOCAL_AGENT_NODE_DEFAULT_POLL_TIMEOUT_MS,
  type LocalAgentNodeSessionLifecycleRequest,
  type LocalAgentNodeSessionPollResponse,
  type LocalAgentNodeSessionProgressRequest,
  type LocalAgentNodeSessionRegistrationRequest,
  type LocalAgentNodeSessionRegistrationResponse,
  type LocalAgentNodeSessionResponseRequest,
} from './local-agent-node-protocol'

type BridgeEventTarget = Pick<EventTarget, 'addEventListener' | 'removeEventListener'>

export interface LocalAgentNodeClientOptions {
  baseUrl?: string
  eventTarget?: BridgeEventTarget
  pollTimeoutMs?: number
  sessionId?: string
  appVersion?: string
  maxRememberedRequestIds?: number
}

function createInitialLifecyclePayload(): LocalAgentBridgeLifecyclePayload {
  const context = createAssistantToolContext()
  return {
    type: 'app-ready',
    currentProjectId: context.currentProjectId,
    currentWorkspaceName: context.currentWorkspaceName,
    currentEditorWorkspace: context.currentEditorWorkspace,
    isUserSignedIn: context.isUserSignedIn,
    subscriptionTier: context.subscriptionTier,
    protocolVersion: LOCAL_AGENT_PROTOCOL_VERSION,
  }
}

export class LocalAgentNodeClient {
  private readonly baseUrl: string
  private readonly eventTarget: BridgeEventTarget
  private readonly bridge = getLocalAgentBridge()
  private readonly sessionId: string
  private readonly appVersion: string
  private readonly maxRememberedRequestIds: number
  private pollTimeoutMs: number
  private running = false
  private lastLifecyclePayload = createInitialLifecyclePayload()
  private readonly dispatchedRequestIds = new Set<string>()
  private readonly dispatchedRequestIdOrder: string[] = []
  private readonly responseListener: EventListener
  private readonly progressListener: EventListener
  private readonly lifecycleListener: EventListener
  private readonly samplingRequestListener: EventListener

  constructor(options: LocalAgentNodeClientOptions = {}) {
    this.baseUrl = options.baseUrl ?? LOCAL_AGENT_NODE_DEFAULT_BASE_URL
    this.eventTarget = options.eventTarget ?? window
    this.pollTimeoutMs = options.pollTimeoutMs ?? LOCAL_AGENT_NODE_DEFAULT_POLL_TIMEOUT_MS
    this.sessionId = options.sessionId ?? crypto.randomUUID()
    this.appVersion = options.appVersion ?? LOCAL_AGENT_PROTOCOL_VERSION
    this.maxRememberedRequestIds = Math.max(1, options.maxRememberedRequestIds ?? 200)
    this.responseListener = ((event: Event) => {
      const detail = (event as CustomEvent<LocalAgentNodeSessionResponseRequest['response']>).detail
      void this
        .postSerializedResponse(detail)
        .catch(() => {})
    }) satisfies EventListener
    this.progressListener = ((event: Event) => {
      const detail = (event as CustomEvent<LocalAgentNodeSessionProgressRequest['progress']>).detail
      void this
        .postJson('/api/local-agent/session/progress', {
          sessionId: this.sessionId,
          progress: detail,
        } satisfies LocalAgentNodeSessionProgressRequest)
        .catch(() => {})
    }) satisfies EventListener
    this.lifecycleListener = ((event: Event) => {
      const detail = (event as CustomEvent<LocalAgentBridgeLifecyclePayload>).detail
      this.lastLifecyclePayload = detail
      void this
        .postJson('/api/local-agent/session/lifecycle', {
          sessionId: this.sessionId,
          lifecycle: detail,
        } satisfies LocalAgentNodeSessionLifecycleRequest)
        .catch(() => {})
    }) satisfies EventListener
    this.samplingRequestListener = ((event: Event) => {
      const detail = (event as CustomEvent<{
        requestId: string
        messages: Array<{
          role: 'user' | 'assistant' | 'system'
          content: Array<{
            type: 'text'
            text: string
          }>
        }>
        systemPrompt?: string
        temperature?: number
        maxTokens?: number
        protocolVersion: string
      }>).detail
      void this.handleSamplingRequest(detail).catch(() => {})
    }) satisfies EventListener
  }

  start(): void {
    if (this.running) {
      return
    }

    this.running = true
    this.eventTarget.addEventListener(LOCAL_AGENT_EVENT_NAMES.response, this.responseListener)
    this.eventTarget.addEventListener(LOCAL_AGENT_EVENT_NAMES.progress, this.progressListener)
    this.eventTarget.addEventListener(LOCAL_AGENT_EVENT_NAMES.lifecycle, this.lifecycleListener)
    this.eventTarget.addEventListener(
      LOCAL_AGENT_EVENT_NAMES.samplingRequest,
      this.samplingRequestListener,
    )
    void this.run()
  }

  stop(): void {
    this.running = false
    this.eventTarget.removeEventListener(LOCAL_AGENT_EVENT_NAMES.response, this.responseListener)
    this.eventTarget.removeEventListener(LOCAL_AGENT_EVENT_NAMES.progress, this.progressListener)
    this.eventTarget.removeEventListener(LOCAL_AGENT_EVENT_NAMES.lifecycle, this.lifecycleListener)
    this.eventTarget.removeEventListener(
      LOCAL_AGENT_EVENT_NAMES.samplingRequest,
      this.samplingRequestListener,
    )
  }

  private async handleSamplingRequest(detail: {
    requestId: string
    messages: Array<{
      role: 'user' | 'assistant' | 'system'
      content: Array<{
        type: 'text'
        text: string
      }>
    }>
    systemPrompt?: string
    temperature?: number
    maxTokens?: number
    protocolVersion: string
  }): Promise<void> {
    try {
      const response = await fetch(`${this.baseUrl}/api/local-agent/sampling`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId: this.sessionId,
          request: detail,
        }),
      })

      if (!response.ok) {
        throw new Error(`Sampling bridge request failed with status ${response.status}.`)
      }

      const result = await response.json()
      if (result.ok) {
        this.eventTarget.dispatchEvent(
          createLocalAgentSamplingResponseEvent({
            requestId: detail.requestId,
            ok: true,
            result: result.result,
            protocolVersion: LOCAL_AGENT_PROTOCOL_VERSION,
          }),
        )
      } else {
        this.eventTarget.dispatchEvent(
          createLocalAgentSamplingResponseEvent({
            requestId: detail.requestId,
            ok: false,
            error: result.error,
            protocolVersion: LOCAL_AGENT_PROTOCOL_VERSION,
          }),
        )
      }
    } catch (error) {
      const normalizedError = normalizeAssistantToolError(error)
      this.eventTarget.dispatchEvent(
        createLocalAgentSamplingResponseEvent({
          requestId: detail.requestId,
          ok: false,
          error: normalizedError.toJSON(),
          protocolVersion: LOCAL_AGENT_PROTOCOL_VERSION,
        }),
      )
    }
  }

  private async run(): Promise<void> {
    while (this.running) {
      try {
        await this.ensureRegistered()
        const pollResponse = await this.fetchJson<LocalAgentNodeSessionPollResponse>(
          `/api/local-agent/session/next?sessionId=${encodeURIComponent(this.sessionId)}&timeoutMs=${this.pollTimeoutMs}`,
        )

        if (pollResponse.request && this.shouldDispatchRequest(pollResponse.request.requestId)) {
          this.rememberDispatchedRequestId(pollResponse.request.requestId)
          this.bridge.dispatchRequest(pollResponse.request)
        }
      } catch {
        await this.sleep(1_500)
      }
    }
  }

  private async ensureRegistered(): Promise<void> {
    const registration = await this.postJson<LocalAgentNodeSessionRegistrationResponse>(
      '/api/local-agent/session/register',
      {
        sessionId: this.sessionId,
        appOrigin: window.location.origin,
        appVersion: this.appVersion,
        lifecycle: this.lastLifecyclePayload,
      } satisfies LocalAgentNodeSessionRegistrationRequest,
    )

    this.pollTimeoutMs = registration.pollTimeoutMs
  }

  private async postSerializedResponse(
    detail: LocalAgentNodeSessionResponseRequest['response'],
  ): Promise<void> {
    const response = detail.ok
      ? {
          ...detail,
          result: await serializeAssistantTransportValue(detail.result),
        }
      : detail

    await this.postJson('/api/local-agent/session/response', {
      sessionId: this.sessionId,
      response,
    } satisfies LocalAgentNodeSessionResponseRequest)
  }

  private async fetchJson<T>(path: string): Promise<T> {
    const response = await fetch(`${this.baseUrl}${path}`)
    if (!response.ok) {
      throw new Error(`Local node bridge request failed with status ${response.status}.`)
    }

    return (await response.json()) as T
  }

  private async postJson<T>(path: string, body: unknown): Promise<T> {
    const response = await fetch(`${this.baseUrl}${path}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    })

    if (!response.ok) {
      throw new Error(`Local node bridge request failed with status ${response.status}.`)
    }

    return (await response.json()) as T
  }

  private async sleep(durationMs: number): Promise<void> {
    await new Promise((resolve) => {
      window.setTimeout(resolve, durationMs)
    })
  }

  private shouldDispatchRequest(requestId: string): boolean {
    return !this.dispatchedRequestIds.has(requestId)
  }

  private rememberDispatchedRequestId(requestId: string): void {
    if (this.dispatchedRequestIds.has(requestId)) {
      return
    }

    this.dispatchedRequestIds.add(requestId)
    this.dispatchedRequestIdOrder.push(requestId)

    while (this.dispatchedRequestIdOrder.length > this.maxRememberedRequestIds) {
      const expiredRequestId = this.dispatchedRequestIdOrder.shift()
      if (!expiredRequestId) {
        break
      }
      this.dispatchedRequestIds.delete(expiredRequestId)
    }
  }
}
